import { useEffect, useRef } from 'react';
import { InfoTooltip } from './InfoTooltip';

/**
 * A semicircular "speedometer" meter: five coloured bands, a needle, and the
 * band name in text under the value (design system §5 — never colour alone).
 * Bands are drawn at equal widths so every label stays readable; the needle
 * is placed proportionally *within* its band, so uneven thresholds (e.g.
 * 0–10–20–35–50–100 %) still read correctly.
 *
 * Colours are the design system's status/accent palette (§1.2, §1.5):
 * red → coral → yellow → green tint → green for "higher is better"; pass
 * `higherIsBetter={false}` to reverse the palette for a cost-like measure.
 */

export interface GaugeBand {
  /** Upper bound of the band (inclusive), in the gauge's value units. */
  upTo: number;
  label: string;
}

const BAND_COLORS = ['#F53B53', '#F4845F', '#FFD166', '#6AE6C6', '#06D6A0'] as const;

/** Sensible default bands for a 0–100 % efficiency figure. */
export const PERCENT_BANDS: GaugeBand[] = [
  { upTo: 20, label: 'Low' },
  { upTo: 40, label: 'Fair' },
  { upTo: 60, label: 'Moderate' },
  { upTo: 80, label: 'Good' },
  { upTo: 100, label: 'Excellent' },
];

const CX = 120;
const CY = 118;
const OUTER = 100;
const INNER = 64;
const GAP_DEGREES = 1.6;

function polar(radius: number, angleDegrees: number): [number, number] {
  // 180° is the left end of the arc, 0° the right end.
  const radians = (angleDegrees * Math.PI) / 180;
  return [CX + radius * Math.cos(radians), CY - radius * Math.sin(radians)];
}

function bandPath(startDeg: number, endDeg: number): string {
  const [x1, y1] = polar(OUTER, startDeg);
  const [x2, y2] = polar(OUTER, endDeg);
  const [x3, y3] = polar(INNER, endDeg);
  const [x4, y4] = polar(INNER, startDeg);
  return `M ${x1} ${y1} A ${OUTER} ${OUTER} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${INNER} ${INNER} 0 0 0 ${x4} ${y4} Z`;
}

/** Maps a value onto 0..1 of the arc, band by band. */
function needlePosition(value: number, bands: GaugeBand[], min: number): { fraction: number; bandIndex: number } {
  const clamped = Math.min(Math.max(value, min), bands[bands.length - 1].upTo);
  let lower = min;
  for (let index = 0; index < bands.length; index += 1) {
    const upper = bands[index].upTo;
    if (clamped <= upper || index === bands.length - 1) {
      const within = upper > lower ? (clamped - lower) / (upper - lower) : 0;
      return { fraction: (index + within) / bands.length, bandIndex: index };
    }
    lower = upper;
  }
  return { fraction: 1, bandIndex: bands.length - 1 };
}

function useNeedleAnimation(fraction: number | null) {
  const needleRef = useRef<SVGPolygonElement>(null);

  useEffect(() => {
    const needle = needleRef.current;
    if (!needle || fraction === null) return;

    const targetRotation = `${fraction * 180}deg`;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      needle.style.transform = `rotate(${targetRotation})`;
      return;
    }

    const animation = needle.animate(
      [{ transform: 'rotate(0deg)' }, { transform: `rotate(${targetRotation})` }],
      { duration: 1000, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' },
    );
    return () => animation.cancel();
  }, [fraction]);

  return needleRef;
}

/**
 * The same meter at KPI-card size (no card chrome, no headline) — for a
 * percentage that already has its number printed next to it. The band name is
 * exposed as the meter's text value and tooltip.
 */
export function MiniGauge({
  value,
  label,
  score,
  prominent = false,
  bands = PERCENT_BANDS,
  min = 0,
  higherIsBetter = true,
}: {
  value: number | null;
  label: string;
  score?: string;
  prominent?: boolean;
  bands?: GaugeBand[];
  min?: number;
  higherIsBetter?: boolean;
}) {
  const colors = higherIsBetter ? BAND_COLORS : [...BAND_COLORS].reverse();
  const sweep = 180 / bands.length;
  const known = value !== null && Number.isFinite(value);
  const position = known ? needlePosition(value, bands, min) : null;
  const needleRef = useNeedleAnimation(position?.fraction ?? null);
  const [tipX, tipY] = polar(OUTER - 8, 180);
  const [baseLeftX, baseLeftY] = polar(9, 270);
  const [baseRightX, baseRightY] = polar(9, 90);
  const bandLabel = position ? bands[position.bandIndex].label : 'No data';

  return (
    <span
      className={prominent ? 'mx-auto block w-full max-w-[260px]' : 'inline-block w-[72px] shrink-0'}
      role="meter"
      aria-label={label}
      aria-valuemin={min}
      aria-valuemax={bands[bands.length - 1].upTo}
      aria-valuenow={known ? value : undefined}
      aria-valuetext={bandLabel}
      title={bandLabel}
    >
      <svg viewBox="0 0 240 132" className="h-auto w-full" aria-hidden="true">
        {bands.map((band, index) => {
          const start = 180 - index * sweep - (index === 0 ? 0 : GAP_DEGREES);
          const end = 180 - (index + 1) * sweep + (index === bands.length - 1 ? 0 : GAP_DEGREES);
          return (
            <path
              key={band.label}
              d={bandPath(start, end)}
              fill={colors[index % colors.length]}
              opacity={!known ? 0.25 : position?.bandIndex === index ? 1 : 0.5}
            />
          );
        })}
        {known && (
          <>
            <polygon
              ref={needleRef}
              points={`${tipX},${tipY} ${baseLeftX},${baseLeftY} ${baseRightX},${baseRightY}`}
              fill="var(--text-default)"
              style={{ transformBox: 'view-box', transformOrigin: `${CX}px ${CY}px` }}
            />
            <circle cx={CX} cy={CY} r={14} fill="var(--text-default)" />
          </>
        )}
      </svg>
      {prominent && score && (
        <span className="mt-1 block text-center text-[28px] font-bold leading-tight tabular-nums text-text-default">{score}</span>
      )}
      <span className={`block text-center font-medium uppercase tracking-wide text-text-muted ${prominent ? 'text-xs' : 'text-[10px]'}`}>
        {bandLabel}
      </span>
    </span>
  );
}

export function EfficiencyGauge({
  label,
  value,
  valueLabel,
  meta,
  info,
  bands = PERCENT_BANDS,
  min = 0,
  higherIsBetter = true,
  size = 'md',
}: {
  label: string;
  /** Null renders an empty, needle-less gauge with "—" (no data), never a misleading zero. */
  value: number | null;
  /** Headline text; defaults to the value as a percentage. */
  valueLabel?: string;
  meta?: string;
  info?: string;
  bands?: GaugeBand[];
  min?: number;
  higherIsBetter?: boolean;
  size?: 'sm' | 'md';
}) {
  const colors = higherIsBetter ? BAND_COLORS : [...BAND_COLORS].reverse();
  const sweep = 180 / bands.length;
  const known = value !== null && Number.isFinite(value);
  const position = known ? needlePosition(value, bands, min) : null;
  const needleRef = useNeedleAnimation(position?.fraction ?? null);
  const [tipX, tipY] = polar(OUTER - 8, 180);
  const [baseLeftX, baseLeftY] = polar(7, 270);
  const [baseRightX, baseRightY] = polar(7, 90);
  const headline = known ? (valueLabel ?? `${value.toFixed(1)}%`) : '—';
  const bandLabel = position ? bands[position.bandIndex].label : 'No data';
  const width = size === 'sm' ? 'max-w-[200px]' : 'max-w-[260px]';

  return (
    <div className="relative flex h-full flex-col rounded-card border border-border bg-surface p-5 shadow-sm">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
        {label}
        {info && <InfoTooltip text={info} />}
      </p>
      <div
        className={`mx-auto mt-2 w-full ${width}`}
        role="meter"
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={bands[bands.length - 1].upTo}
        aria-valuenow={known ? value : undefined}
        aria-valuetext={known ? `${headline}, ${bandLabel}` : 'No data'}
      >
        <svg viewBox="0 0 240 132" className="h-auto w-full" aria-hidden="true">
          {bands.map((band, index) => {
            const start = 180 - index * sweep - (index === 0 ? 0 : GAP_DEGREES / 2);
            const end = 180 - (index + 1) * sweep + (index === bands.length - 1 ? 0 : GAP_DEGREES / 2);
            const active = position?.bandIndex === index;
            return (
              <path
                key={band.label}
                d={bandPath(start, end)}
                fill={colors[index % colors.length]}
                opacity={!known ? 0.25 : active ? 1 : 0.55}
              />
            );
          })}
          {known && (
            <>
              <polygon
                ref={needleRef}
                points={`${tipX},${tipY} ${baseLeftX},${baseLeftY} ${baseRightX},${baseRightY}`}
                fill="var(--text-default)"
                style={{ transformBox: 'view-box', transformOrigin: `${CX}px ${CY}px` }}
              />
              <circle cx={CX} cy={CY} r={11} fill="var(--text-default)" />
              <circle cx={CX} cy={CY} r={5} fill="var(--surface)" />
            </>
          )}
        </svg>
      </div>
      <p className="mt-1 text-center text-[28px] font-bold leading-tight tabular-nums text-text-default">{headline}</p>
      <p className="text-center text-xs font-medium uppercase tracking-wide text-text-muted">{bandLabel}</p>
      {meta && <p className="mt-1.5 text-center text-xs text-text-muted">{meta}</p>}
    </div>
  );
}
